import { MoldOrderType, MoldOrderFirstLevelType, OrderDetail } from '../../types/mold';
import { BaseMoldOrderModel } from './base';

/**
 * Mold Acceptance order model
 * Handles mold acceptance/incoming order types and their specific logic
 */
export class MoldAcceptanceMoldOrderModel extends BaseMoldOrderModel {
  readonly type: MoldOrderFirstLevelType = 'mold_acceptance';
  readonly label = 'รับเข้าแม่พิมพ์';
  readonly description = 'ใบงานสำหรับการรับเข้าแม่พิมพ์ใหม่เข้าสู่ระบบ';
  readonly iconColor = 'text-green-600';
  readonly headerBgColor = 'bg-gradient-to-br from-green-50 to-green-100/70';
  readonly headerBorderColor = 'border-green-200/60';
  readonly headerShadow = 'shadow-lg shadow-green-100/20';
  readonly headerTextColor = 'text-green-900';

  getDefaultMoldOrderType(): MoldOrderType {
    return MoldOrderType.MOLD_ACCEPTANCE;
  }

  getAvailableMoldOrderTypes(): MoldOrderType[] {
    return [MoldOrderType.MOLD_ACCEPTANCE];
  }

  createInitialOrderDetail(): OrderDetail {
    return {
      expectedArrivalDate: this.getDefaultDueDate(1), // Give 1 day for acceptance process
      moldOrderType: this.getDefaultMoldOrderType(),
      note: '',
      form: {},
    };
  }

  validateOrderDetail(orderDetail: OrderDetail): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Basic validation
    if (!orderDetail.dueDate) {
      errors.push('กรุณาระบุวันที่ครบกำหนด');
    }

    if (!orderDetail.moldOrderType) {
      errors.push('กรุณาเลือกประเภทงาน');
    }

    // Mold acceptance specific validation
    if (this.requiresAcceptanceChecklist(orderDetail.moldOrderType)) {
      if (!this.hasCompletedAcceptanceChecklist(orderDetail.form)) {
        errors.push('กรุณากรอกรายการตรวจสอบการรับเข้าแม่พิมพ์ให้ครบถ้วน');
      }
    }

    // Storage location is typically required for new molds
    if (this.requiresStorageLocation(orderDetail.moldOrderType)) {
      if (!orderDetail.storageLocation && !orderDetail.customStorageLocation) {
        errors.push('กรุณาระบุตำแหน่งที่เก็บแม่พิมพ์');
      }
    }

    // Shot count validation for new molds
    if (orderDetail.accumulativeShotCount !== undefined && orderDetail.accumulativeShotCount < 0) {
      errors.push('จำนวนช๊อตไม่สามารถเป็นค่าลบได้');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  getRequiredFormFields(): string[] {
    return ['moldOrderType', 'expectedArrivalDate'];
  }

  requiresStorageLocation(_moldOrderType: MoldOrderType): boolean {
    // New molds typically need storage location
    return false;
  }

  requiresShiftCompletedInfo(_moldOrderType: MoldOrderType): boolean {
    // Acceptance orders might not need specific shift info
    return false;
  }

  requiresMachineToProduce(_moldOrderType: MoldOrderType): boolean {
    // Acceptance orders don't typically need machine assignment yet
    return false;
  }

  /**
   * Check if acceptance checklist is required
   */
  requiresAcceptanceChecklist(moldOrderType: MoldOrderType): boolean {
    return moldOrderType === MoldOrderType.MOLD_ACCEPTANCE;
  }

  /**
   * Check if acceptance checklist is completed
   */
  hasCompletedAcceptanceChecklist(form: Record<string, any>): boolean {
    const requiredFields = this.getAcceptanceChecklistFields();
    return requiredFields.every(field => form[field] !== undefined && form[field] !== '');
  }

  /**
   * Get required fields for acceptance checklist
   */
  getAcceptanceChecklistFields(): string[] {
    return [
      'moldCondition',
      'documentationComplete',
      'dimensionVerified',
      'markingVerified',
      'moldWeight',
      'moldDimensions',
    ];
  }

  /**
   * Create initial order detail with acceptance-specific logic
   */
  createInitialOrderDetailForAcceptance(): OrderDetail {
    const base = this.createInitialOrderDetail();
    
    return base;
  }

  /**
   * Get acceptance-specific validation rules
   */
  getAcceptanceValidationRules() {
    return {
      checklistRequired: (orderDetail: OrderDetail) => 
        this.requiresAcceptanceChecklist(orderDetail.moldOrderType),
      checklistComplete: (orderDetail: OrderDetail) => 
        this.hasCompletedAcceptanceChecklist(orderDetail.form),
      storageLocationRequired: (orderDetail: OrderDetail) => 
        this.requiresStorageLocation(orderDetail.moldOrderType),
      shotCountValid: (orderDetail: OrderDetail) => 
        orderDetail.accumulativeShotCount === undefined || orderDetail.accumulativeShotCount >= 0,
    };
  }

  /**
   * Get acceptance status based on form completion
   */
  getAcceptanceStatus(orderDetail: OrderDetail): 'pending' | 'in_progress' | 'completed' | 'rejected' {
    if (!this.hasCompletedAcceptanceChecklist(orderDetail.form)) {
      return 'pending';
    }

    const form = orderDetail.form;
    const criticalChecks = ['documentationComplete', 'dimensionVerified', 'markingVerified'];
    const failedChecks = criticalChecks.filter(check => !form[check]);

    if (failedChecks.length > 0) {
      return 'rejected';
    }

    return 'completed';
  }
}
